import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal, Input, Button, Select } from "../../../components/ui";
import { ADD_HCP_ASSIGN, GET_HOSPITAL, UPDATE_HCP_ASSIGN, GET_COUNTRY_LIST } from "../../../api/url";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { toast } from "react-toastify";
import { strings } from "../../../strings/strings";
import FormPhoneInput from "../../../components/ui/FormPhoneInput";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { AsYouType, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";

const normalize = (v) => (typeof v === "string" ? v.trim() : v);
const onlyDigits = (value = "") => (value || "").replace(/\D/g, "");
const formatPhoneInternationally = (number = "", country = "", dialCode = "") => {
  if (!number) return "";
  const digits = onlyDigits(number);
  if (!digits) return "";

  try {
    const internationalAsYouType = new AsYouType();
    internationalAsYouType.input(dialCode || "");
    const formatted = internationalAsYouType.input(digits);

    if (dialCode && formatted.startsWith(dialCode)) {
      return formatted.slice(dialCode.length).trim();
    }
    return formatted;
  } catch (error) {
    return digits;
  }
};

const buildChangedPayload = (initial, current) => {
  const changed = {};
  Object.keys(current).forEach((key) => {
    const a = normalize(initial?.[key] ?? "");
    const b = normalize(current?.[key] ?? "");
    if (a !== b) changed[key] = b;
  });
  return changed;
};

const validationSchema = Yup.object({
  firstName: Yup.string()
    .required(strings.firstNameRequired)
    .min(2, strings.firstNameMin)
    .max(50, strings.firstNameMax)
    .matches(/^[a-zA-Z\s'.-]+$/, strings.firstNameInvalid),

  lastName: Yup.string()
    .required(strings.lastNameRequired)
    .min(2, strings.lastNameMin)
    .max(50, strings.lastNameMax)
    .matches(/^[a-zA-Z\s'.-]+$/, strings.lastNameInvalid),

  email: Yup.string().email(strings.emailInvalid).max(100, strings.emailMax),

  hospitalId: Yup.string().required(strings.hospitalRequired),
  mobile_code: Yup.string().required(strings.mobileRequired),
  mobile_number: Yup.string()
    .required(strings.mobileNumberRequired)
    .test("valid-mobile-number", strings.mobileInvalid || "", function (value) {
      const { mobile_code, mobile_country } = this.parent;

      const rawNumber = onlyDigits(value || "");
      const rawCode = (mobile_code || "").trim();

      if (!rawNumber || !rawCode) return false;

      try {
        if (mobile_country) {
          return isValidPhoneNumber(rawNumber, mobile_country.toUpperCase());
        }
        return isValidPhoneNumber(`${rawCode}${rawNumber}`);
      } catch (error) {
        return false;
      }
    }),
});
const AddHcpsModal = ({ isOpen, onClose, onSuccess, mode = "add", hcp = null }) => {
  const mutation = useAppMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const [hospitalsList, setHospitalsList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 10;
  const { data: hospitalRes, isFetching: isHospitalFetching } = useAppQuery({
    queryKey: [...QUERY_KEYS.HOSPITAL, currentPage],
    url: GET_HOSPITAL,
    method: "GET",
    enabled: isOpen,
    params: { page: currentPage, limit: itemsPerPage },
  });

  const { data: countryListRes } = useAppQuery({
    queryKey: [QUERY_KEYS.COUNTRY_LIST],
    url: GET_COUNTRY_LIST,
    method: "GET",
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    const newData = hospitalRes?.results?.data || (Array.isArray(hospitalRes?.results) ? hospitalRes.results : null);
    if (newData) {
      setHospitalsList((prev) => {
        const existingIds = new Set(prev.map((h) => String(h.id)));
        const filteredNewData = newData.filter((h) => !existingIds.has(String(h.id)));
        return [...prev, ...filteredNewData];
      });

      const pagination = hospitalRes?.results?.pagination;
      if (pagination) {
        setHasMore(currentPage < pagination.totalPages);
      } else {
        setHasMore(newData.length >= itemsPerPage);
      }
    }
  }, [hospitalRes, currentPage, itemsPerPage, isOpen]);

  const handleMenuScroll = () => {
    if (hasMore && !isHospitalFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const hospitals = hospitalsList;

  const allowedCountries = useMemo(() => {
    if (!countryListRes?.results) return [];
    return countryListRes.results.map((country) => country.country_code?.toLowerCase()).filter(Boolean);
  }, [countryListRes]);

  const emptyValues = {
    firstName: "",
    lastName: "",
    email: "",
    hospitalId: "",
    mobile_code: "",
    mobile_country: "",
    mobile_number: "",
    mode,
  };

  const editInitialValues = useMemo(() => {
    let mobileCountry = hcp?.mobile_country || "";
    const mobileNumber = hcp?.mobile_number || "";
    const mobileCode = hcp?.mobile_code || "";

    if (!mobileCountry && mobileCode && mobileNumber) {
      try {
        const fullNumber = mobileCode.startsWith("+")
          ? `${mobileCode}${mobileNumber}`
          : `+${mobileCode}${mobileNumber}`;
        const parsed = parsePhoneNumberFromString(fullNumber);
        if (parsed && parsed.country) {
          mobileCountry = parsed.country;
        }
      } catch (e) {}
    }

    return {
      firstName: hcp?.first_name || "",
      lastName: hcp?.last_name || "",
      email: hcp?.email || "",
      hospitalId: String(hcp?.hospital_id || ""),
      mobile_code: mobileCode,
      mobile_country: mobileCountry,
      mobile_number: formatPhoneInternationally(mobileNumber, mobileCountry, mobileCode),
      mode,
    };
  }, [mode, hcp]);

  const formik = useFormik({
    initialValues: emptyValues,
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const isEdit = mode === "edit";
        const hcpId = hcp?.id || hcp?._id;
        const rawMobileNumber = onlyDigits(values.mobile_number);

        if (!isEdit) {
     const payload = {
       first_name: normalize(values.firstName),
       last_name: normalize(values.lastName),
       hospital_id: values.hospitalId,
       mobileCode: normalize(values.mobile_code),
       mobileNumber: rawMobileNumber,
       ...(normalize(values.email) ? { email: normalize(values.email) } : {}),
     };

          const res = await mutation.mutateAsync({
            url: ADD_HCP_ASSIGN,
            method: "POST",
            data: payload,
          });

          toast.success(res?.message);
          resetForm();
          onSuccess?.();
          return;
        }

        if (!hcpId) {
          toast.error(strings.hcpIdNotFound);
          return;
        }

        const currentEditable = {
          first_name: normalize(values.firstName),
          last_name: normalize(values.lastName),
          hospital_id: normalize(values.hospitalId),
          mobileCode: normalize(values.mobile_code),
          mobileNumber: rawMobileNumber,
          email: normalize(values.email || ""),
        };
        const initialEditable = {
          first_name: normalize(hcp?.first_name || ""),
          last_name: normalize(hcp?.last_name || ""),
          hospital_id: normalize(String(hcp?.hospital_id || "")),
          mobileCode: normalize(hcp?.mobile_code || ""),
          mobileNumber: normalize(onlyDigits(hcp?.mobile_number || "")),
          email: normalize(hcp?.email || ""),
        };
        const changedPayload = buildChangedPayload(initialEditable, currentEditable);

        if (Object.keys(changedPayload).length === 0) {
          toast.info(strings.noChangesToUpdate);
          return;
        }

        const res = await mutation.mutateAsync({
          url: `${UPDATE_HCP_ASSIGN}?id=${hcpId}`,
          method: "PUT",
          data: changedPayload,
        });

        toast.success(res?.message);
        resetForm();
        onSuccess?.();
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setHospitalsList([]);
      setCurrentPage(1);
      setHasMore(true);
      return;
    }

    if (mode === "edit" && hcp) {
      formik.setValues(editInitialValues);
      formik.setTouched({});
      formik.setErrors({});
    } else {
      formik.resetForm();
      formik.setFieldValue("mode", mode);

      if (allowedCountries.length > 0) {
        const iso = allowedCountries[0].toUpperCase();
        formik.setFieldValue("mobile_country", iso);

        try {
          const dialCode = `+${getCountryCallingCode(iso)}`;
          formik.setFieldValue("mobile_code", dialCode);
        } catch (error) {}
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, hcp, editInitialValues, allowedCountries]);

  const handleClose = () => {
    formik.resetForm();
    onClose?.();
  };
const selectedHospital = useMemo(() => {
  return hospitals.find((h) => String(h.id) === String(formik.values.hospitalId));
}, [hospitals, formik.values.hospitalId]);

const selectedReferralCode = selectedHospital?.referral_code || "-";

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={handleClose} disabled={formik.isSubmitting}>
        {strings.cancel}
      </Button>

      <Button
        variant="primary"
        onClick={formik.handleSubmit}
        disabled={mutation.isPending || !formik.isValid}
        isLoading={mutation.isPending}
      >
        {mode === "edit" ? strings.update : strings.addHcp}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === "edit" ? strings.editHcpTitle : strings.addHcpTitle}
      footer={footer}
      size="md"
    >
      <form onSubmit={formik.handleSubmit} className="space-y-5">
        <Select
          label={strings.hospitalName}
          name="hospitalId"
          required
          placeholder={strings.selectHospital}
          value={formik.values.hospitalId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.hospitalId}
          touched={formik.touched.hospitalId}
          onMenuScroll={handleMenuScroll}
          options={hospitals.map((h) => ({
            label: h.name,
            value: String(h.id),
          }))}
        />
        {formik.values.hospitalId && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm text-gray-500">{strings.hospitalRefferalCode}</p>
            <p className="text-sm font-semibold text-secondary">{selectedReferralCode || "-"}</p>
          </div>
        )}
        <Input
          label={strings.firstName}
          name="firstName"
          required
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.firstName}
          touched={formik.touched.firstName}
          placeholder={strings.enterFirstName}
        />

        <Input
          label={strings.lastName}
          name="lastName"
          required
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.lastName}
          touched={formik.touched.lastName}
          placeholder={strings.enterLastName}
        />

        <div className="grid grid-cols-3 gap-3">
          {allowedCountries.length > 0 ? (
            <FormPhoneInput
              label={strings.code}
              required
              mode="dialCode"
              name="mobile_code"
              value={formik.values.mobile_code}
              touched={formik.touched.mobile_code}
              error={formik.errors.mobile_code}
              onBlur={() => formik.setFieldTouched("mobile_code", true)}
              onCodeChange={(code, countryCode) => {
                const iso = countryCode?.toUpperCase() || "";

                formik.setFieldValue("mobile_code", code, true);
                formik.setFieldValue("mobile_country", iso, true);
                formik.setFieldValue("mobile_number", "", true);

                formik.setFieldTouched("mobile_number", false);
                formik.setFieldError("mobile_number", "");
              }}
              country={formik.values.mobile_country?.toLowerCase() || undefined}
              onlyCountries={allowedCountries}
            />
          ) : (
            <div className="w-full">
              <label className="block text-sm font-medium text-secondary mb-2">
                {strings.code} <span className="text-red-500">*</span>
              </label>
              <div className="w-full h-[46px] bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-sm text-gray-500">{strings.loading}</span>
              </div>
            </div>
          )}

          <div className="col-span-2">
            <Input
              label={strings.mobileNumber2}
              name="mobile_number"
              required
              value={formik.values.mobile_number}
              onChange={(e) => {
                const grouped = formatPhoneInternationally(
                  e.target.value,
                  formik.values.mobile_country,
                  formik.values.mobile_code,
                );

                formik.setFieldTouched("mobile_number", true, false);
                formik.setFieldValue("mobile_number", grouped, true);
              }}
              onBlur={(e) => {
                formik.handleBlur(e);
                formik.setFieldTouched("mobile_number", true, true);
              }}
              error={formik.errors.mobile_number}
              touched={formik.touched.mobile_number}
              placeholder={strings.enterMobileNumber}
              inputMode="numeric"
            />
          </div>
        </div>

        <Input
          label={strings.email}
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.email}
          touched={formik.touched.email}
          placeholder={strings.enterEmail}
        />
      </form>
    </Modal>
  );
};

export default AddHcpsModal;
